/**
 * ResponsivePreview
 *
 * Viewport toggle + device-framed preview container.
 * Export: PreviewToolbar (toggle buttons) + ResponsivePreview (wrapper).
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Tablet, Smartphone } from 'lucide-react';

/* ── Types ──────────────────────────────────────────────────────── */

export type PreviewMode = 'desktop' | 'tablet' | 'mobile';

export interface PreviewToolbarProps {
  mode: PreviewMode;
  onChange: (mode: PreviewMode) => void;
}

export interface ResponsivePreviewProps {
  children: React.ReactNode;
  mode: PreviewMode;
}

const WIDTHS: Record<PreviewMode, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

/* ── PreviewToolbar ─────────────────────────────────────────────── */

const MODES: { key: PreviewMode; Icon: React.ElementType; label: string }[] = [
  { key: 'desktop', Icon: Monitor, label: 'Desktop' },
  { key: 'tablet', Icon: Tablet, label: 'Tablet' },
  { key: 'mobile', Icon: Smartphone, label: 'Mobile' },
];

export function PreviewToolbar({ mode, onChange }: PreviewToolbarProps) {
  return (
    <div className="flex items-center bg-gray-100 rounded-full p-1 gap-1">
      {MODES.map(({ key, Icon, label }) => (
        <button
          key={key}
          type="button"
          title={label}
          onClick={() => onChange(key)}
          className={
            mode === key
              ? 'flex items-center gap-1.5 bg-white shadow-sm rounded-full px-3 py-1 text-[#242424] text-xs font-medium transition-all'
              : 'flex items-center gap-1.5 px-3 py-1 text-gray-500 text-xs hover:text-[#242424] transition-colors'
          }
        >
          <Icon size={13} />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

/* ── ResponsivePreview ──────────────────────────────────────────── */

export function ResponsivePreview({ children, mode }: ResponsivePreviewProps) {
  if (mode === 'desktop') {
    return <>{children}</>;
  }

  return (
    <div className="flex justify-center bg-gray-100 p-6 rounded-lg min-h-full">
      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1, width: WIDTHS[mode] }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ width: WIDTHS[mode], maxWidth: '100%' }}
          className="bg-white shadow-2xl rounded-[2rem] overflow-hidden mx-auto flex flex-col"
        >
          {/* Status bar / notch */}
          <div className="h-6 bg-gray-900 flex items-center justify-center shrink-0">
            <div className="w-16 h-1.5 bg-white/30 rounded-full" />
          </div>

          {/* Content */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: '70vh' }}
          >
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
